import { Close } from "@mui/icons-material";
import { ClickAwayListener, Grow, IconButton, Popper } from "@mui/material";
import { FC, KeyboardEvent, MouseEvent, useMemo } from "react";
import { useTranslation } from "react-i18next";

import {
  DateYMDString,
  ICoursesCalendarSlotDto,
  timezoneAdjustedDateToISO,
  useFormats,
  useTimezone,
} from "@ryde/shared";

import { CoursesCalendarCellExpandPopperPaper } from "../courses-calendar.styled";
import { CoursesCalendarCellFooter } from "./courses-calendar-cell.footer";
import { CoursesCalendarCellSlotItem } from "./courses-calendar-cell.slot-item";
import { formatCoursesCalendarHourLabel } from "./courses-calendar-cell.utils";
import { IUseCoursesCalendarCellExpandResult } from "./use-courses-calendar-cell-expand";

interface ICoursesCalendarCellExpandPopperProps {
  expandState: IUseCoursesCalendarCellExpandResult;
  dateKey: DateYMDString;
  hourIndex: number;
  slots: ICoursesCalendarSlotDto[];
  selectedInCellCount: number;
  isAllInCellSelected: boolean;
  isSlotSelected: (slotId: string) => boolean;
  toggleSlotSelected: (slotId: string) => void;
  setAllInCellSelected: (isSelect: boolean) => void;
  courseOpenClassName?: string;
  onOpenCourseClick: (e: MouseEvent | KeyboardEvent, courseId: number) => void;
  onCreateRideClick: (e: MouseEvent, slot: ICoursesCalendarSlotDto) => void;
  onRideOpen: (rideId: number) => void;
}

export const CoursesCalendarCellExpandPopper: FC<
  ICoursesCalendarCellExpandPopperProps
> = ({
  expandState,
  dateKey,
  hourIndex,
  slots,
  selectedInCellCount,
  isAllInCellSelected,
  isSlotSelected,
  toggleSlotSelected,
  setAllInCellSelected,
  courseOpenClassName,
  onOpenCourseClick,
  onCreateRideClick,
  onRideOpen,
}) => {
  const { t } = useTranslation();
  const { formatDateNormalized } = useFormats();
  const { timezone } = useTimezone();
  const {
    expandAnchorRef,
    popperContentRef,
    isExpandOpen,
    closeExpand,
    forceCloseExpand,
  } = expandState;
  const hasSlotSelection = selectedInCellCount > 0;

  const formattedDate = useMemo(() => {
    const fullIso = timezoneAdjustedDateToISO(dateKey, timezone);
    return formatDateNormalized(fullIso);
  }, [dateKey, formatDateNormalized, timezone]);

  const hourLabel = formatCoursesCalendarHourLabel(hourIndex);
  const title = t("course.calendar.expandCellTitle", {
    date: formattedDate,
    hour: hourLabel,
    count: slots.length,
  });

  return (
    <Popper
      open={isExpandOpen}
      anchorEl={expandAnchorRef.current}
      transition
      placement="bottom-end"
      className="cc-cell-expand-popper"
      modifiers={[
        { name: "flip", enabled: true },
        { name: "preventOverflow", enabled: true },
      ]}
    >
      {({ TransitionProps, placement }) => (
        <Grow
          {...TransitionProps}
          style={{
            transformOrigin:
              placement === "bottom-end" ? "right top" : "right bottom",
          }}
        >
          <CoursesCalendarCellExpandPopperPaper
            elevation={8}
            data-e2e="courses-calendar-cell-expand-popper"
          >
            <ClickAwayListener onClickAway={closeExpand}>
              <div
                ref={popperContentRef}
                className="cc-cell-expand-popper__inner"
              >
                <div className="cc-cell-expand-popper__header">
                  <span className="cc-cell-expand-popper__title">{title}</span>
                  <IconButton
                    size="small"
                    className="cc-cell-expand-popper__close"
                    aria-label={t("course.calendar.expandCellClose")}
                    onClick={event => {
                      event.stopPropagation();
                      forceCloseExpand();
                    }}
                  >
                    <Close fontSize="small" />
                  </IconButton>
                </div>
                <div className="cc-cell-expand-popper__list thin_scrollbar">
                  {slots.map(slot => (
                    <CoursesCalendarCellSlotItem
                      key={slot.id}
                      slot={slot}
                      isSelected={isSlotSelected(slot.id)}
                      onToggleSelected={toggleSlotSelected}
                      tooltipDisabled={false}
                      courseOpenClassName={courseOpenClassName}
                      onOpenCourseClick={onOpenCourseClick}
                      onCreateRideClick={onCreateRideClick}
                      onRideOpen={onRideOpen}
                    />
                  ))}
                </div>
                <CoursesCalendarCellFooter
                  totalCount={slots.length}
                  selectedCount={selectedInCellCount}
                  isAllSelected={isAllInCellSelected}
                  hasSlotSelection={hasSlotSelection}
                  hasOverflow={false}
                  variant="popper"
                  showExpandButton={false}
                  tooltipsDisabled={false}
                  expandAnchorRef={expandAnchorRef}
                  onToggleSelectAll={() =>
                    setAllInCellSelected(!isAllInCellSelected)
                  }
                  onToggleExpand={expandState.toggleExpandOpen}
                />
              </div>
            </ClickAwayListener>
          </CoursesCalendarCellExpandPopperPaper>
        </Grow>
      )}
    </Popper>
  );
};
